import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { apollo } from "../../../../utils/apollo";
import {
  GET_POLICY_LIST,
  GET_FILE_IMPORT_LIST,
} from "../../../../utils/graphql";
import PaginationFull from "../../../../components/PaginationFull";
import TextInput from "../../../../components/TextInput";
import {
  ArrowLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../../../../utils/style";
import CompactPolicyList from "../components/CompactPolicyList";

// ──────────────────────────────────────────────────────────────────────────────
// Stat card
// ──────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, variant = "slate" }) {
  const variants = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
  };
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center rounded-2xl border p-5 gap-1 transition-all",
        variants[variant],
      )}
    >
      <span className="text-3xl font-black">{value ?? "—"}</span>
      <span className="text-[10px] font-black uppercase tracking-widest opacity-70">
        {label}
      </span>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────────
function SlashdataImportResultsView({ fileImport, onBack }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const fileImportUuid = fileImport?.fileImportUuid;

  const batch = fileImport;
  const isProcessing =
    batch?.status === "processing" || batch?.status === "pending";
  const progressPct = batch?.totalRecords
    ? Math.min(
      100,
      Math.max(5, (batch.importedRecords / batch.totalRecords) * 100),
    )
    : 0;

  // ── Policy list ──
  const policyQuery = useQuery({
    queryKey: [
      "slashdata_import_results_policies",
      { page, fileImportUuid, searchTerm, status: batch?.status, imported: batch?.importedRecords },
    ],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_POLICY_LIST,
        variables: {
          page,
          first: 15,
          filter: {
            fileImportUuid: fileImportUuid || null,
            policyUuid: searchTerm || null,
          },
          sortBy: "ID",
          sortDirection: "DESC"
        },
      });
      return data?.getPolicyList;
    },
    enabled: !!fileImportUuid,
    keepPreviousData: true,
  });

  const records = useMemo(
    () => (policyQuery.isSuccess ? policyQuery.data : null),
    [policyQuery],
  );

  return (
    <div className="mt-6 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t("Back to list")}
          </button>
          <span className="text-slate-300">|</span>
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-700">
            {t("Import Results")}
          </h2>
          <span className="text-xs font-mono text-slate-400">
            {fileImportUuid}
          </span>
        </div>
      </div>

      {/* ── Stats + progress ── */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label={t("Total")}
            value={batch?.totalRecords}
            variant="blue"
          />
          <StatCard
            label={t("Imported")}
            value={batch?.importedRecords}
            variant="emerald"
          />
          <StatCard
            label={t("Errors")}
            value={batch?.failedRecords}
            variant="rose"
          />
          <StatCard
            label={t("Status")}
            value={
              <span className="capitalize text-xl font-black">
                {batch?.status}
              </span>
            }
            variant={
              batch?.status === "completed"
                ? "emerald"
                : batch?.status === "failed"
                  ? "rose"
                  : "slate"
            }
          />
        </div>

        {isProcessing && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
              <span className="flex items-center gap-1.5">
                <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                {t("Processing...")}
              </span>
              <span>{Math.round(progressPct)}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(59,130,246,0.4)]"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Search ── */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-4 shadow-sm">
        <div className="max-w-sm">
          <TextInput
            name="results-search"
            label={t("Search")}
            placeholder={t("Policy ID or Reference...")}
            id="results-search"
            get={searchTerm}
            set={setSearchTerm}
          />
        </div>
      </div>

      {/* ── Policy table ── */}
      {records && (
        <>
            <CompactPolicyList
                records={records}
                sort={{ field: "id", isAscending: false }}
                handleChangeSort={() => {}}
                hideBatchButton={true}
            />
            <PaginationFull
                paginator={records?.paginatorInfo}
                get={page}
                set={setPage}
            />
        </>
      )}

      {records?.data && !records.data.length && (
          <div className="flex items-center justify-center h-40 text-slate-400 font-light text-lg bg-white border border-slate-200/60 rounded-2xl shadow-sm">
            {isProcessing
              ? t("Processing policies, results will appear shortly...")
              : t("No policies found.")}
          </div>
      )}
    </div>
  );
}

export default SlashdataImportResultsView;
